import React, { useState, useMemo } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const fallback = (value, type = "number") => {
  if (value === null || value === undefined || value === "") {
    if (type === "percent") return "0.00%";
    if (type === "string") return "-";
    return 0;
  }
  return value;
};

const PlayerStatsModal = ({
  open,
  onClose,
  player,
  leaderboard = [],
  loading,
}) => {
  const [tab, setTab] = useState("bowling");

  const handleTab = (_, newValue) => {
    if (newValue) setTab(newValue);
  };

  const stats = useMemo(() => {
    if (!Array.isArray(leaderboard) || !player) return null;
    return leaderboard.find((p) => p.playerId === player._id) || {};
  }, [leaderboard, player]);

  const statCards = useMemo(() => {
    if (loading) return [];

    if (tab === "bowling") {
      return [
        { label: "MATCHES", value: fallback(stats?.ballMatches) },
        { label: "INNINGS", value: fallback(stats?.ballInnings) },
        { label: "OVERS", value: fallback(stats?.overs) },
        { label: "MAIDIENS", value: fallback(stats?.maidens) },
        { label: "RUNS", value: fallback(stats?.ballRuns) },
        { label: "WKTS", value: fallback(stats?.ballWkts) },
        { label: "BB", value: fallback(stats?.BB) },
        { label: "3 WKTS", value: fallback(stats?.threeWickets) },
        { label: "5 WKTS", value: fallback(stats?.fiveWickets) },
        { label: "ECONOMY", value: fallback(stats?.economy) },
        { label: "STRIKE RATE", value: fallback(stats?.strikeRate) },
        { label: "AVG", value: fallback(stats?.ballAvg) },
        { label: "WIDE", value: fallback(stats?.wide) },
        { label: "NO BALL", value: fallback(stats?.NOs) },
        { label: "DOTS", value: fallback(stats?.dots) },
        { label: "4S", value: fallback(stats?.fours) },
        { label: "6S", value: fallback(stats?.sixes) },
      ];
    }

    if (tab === "batting") {
      return [
        { label: "MATCHES", value: fallback(stats?.matches) },
        { label: "INNINGS", value: fallback(stats?.batInnings) },
        { label: "NO", value: fallback(stats?.NOs) },
        { label: "RUNS", value: fallback(stats?.batRuns) },
        { label: "HS", value: fallback(stats?.HS) },
        { label: "AVG", value: fallback(stats?.batAvg) },
        { label: "SR", value: fallback(stats?.batSR) },
        { label: "30S", value: fallback(stats?.fours) },
        { label: "50S", value: fallback(stats?.bat50s) },
        { label: "100S", value: fallback(stats?.bat100s) },
        { label: "4S", value: fallback(stats?.bat4s) },
        { label: "6S", value: fallback(stats?.bat6s) },
        { label: "DUCKS", value: fallback(stats?.sixes) },
        { label: "WON", value: fallback(stats?.sixes) },
        { label: "LOSS", value: fallback(stats?.sixes) },
      ];
    }

    if (tab === "fielding") {
      return [
        { label: "MATCHES", value: fallback(stats?.fieldMatches) },
        { label: "CATCHES", value: fallback(stats?.fieldCatches) },
        { label: "CB", value: fallback(stats?.fieldCB) },
        { label: "RUN OUTS", value: fallback(stats?.fieldRunOuts) },
        { label: "ST", value: fallback(stats?.fieldST) },
      ];
    }

    return [];
  }, [tab, stats, loading]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: {
            xs: "90%", // mobile
            sm: "70%", // small tablets
            md: "55%", // tablets
            lg: "45%", // laptops
            xl: "40%", // big screens (your current)
          },
          bgcolor: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          height: "35rem",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            borderBottom: "1px solid #eee",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            background: "#fff",
            borderRadius: "16px 16px 0 0",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography fontSize={25} fontWeight={500}>
            Player Stats
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* TABS */}
        <Box
          sx={{
            px: 3,
            pb: 2,
            pt: 2,
            background: "#fff",
            position: "sticky",
            top: 63,
            zIndex: 10,
            borderBottom: "1px solid #eee",
          }}
        >
          <ToggleButtonGroup
            value={tab}
            exclusive
            onChange={handleTab}
            sx={{
              display: "flex",
              flexWrap: { xs: "wrap", sm: "nowrap" },

              "& .MuiToggleButton-root": {
                borderRadius: "20px",
                px: { xs: 1.2, sm: 2 }, // smaller padding on tiny screens
                py: 0.7,
                mx: 0.7,
                textTransform: "none",
                fontFamily: "Urbanist, sans-serif",
                fontSize: { xs: "14px", sm: "19px" }, // smaller text on tiny screens
                color: "#000",
                border: "1px solid #ddd",
                flex: { xs: "1 1 30%", sm: "none" }, // 3 buttons per row on mobile
                textAlign: "center",
              },

              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: "#1e1e1e !important",
                color: "#fff !important",
                borderColor: "#1e1e1e",
              },

              "& .MuiToggleButton-root:hover": {
                backgroundColor: "#eaeaea",
              },
            }}
          >
            <ToggleButton value="batting">Batting</ToggleButton>
            <ToggleButton value="bowling">Bowling</ToggleButton>
            <ToggleButton value="fielding">Fielding</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* BODY */}
        <Box
          sx={{
            overflowY: "auto",
            p: 3,
            pt: 2,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Grid container spacing={2}>
            {statCards.map((item, i) => (
              <Grid item xs={4} sm={4} md={3} key={i}>
                <Box
                  sx={{
                    p: 2,
                    pt: 6,
                    height: 125,
                    width: "100%",
                    maxWidth: 125,
                    margin: "0 auto",
                    borderRadius: "12px",
                    background: "#FFFFFF",
                    boxShadow: "0px 22.61px 22.61px 0px #7E7E7E1C",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "12px", sm: "14px", md: "17px" },
                    }}
                  >
                    {item.value}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      textAlign: "center",
                      color: "#535964",
                      fontSize: { xs: "12px", sm: "14px", md: "17px" },
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default PlayerStatsModal;
